import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useCMS } from '../../context/CMSContext';
import { toast, Toaster } from 'sonner';
import { Plus, Edit2, Trash2, X, Save, Star, Check } from 'lucide-react';

const AdminPricingPage = () => {
  const { getPricing, createPricing, updatePricing, deletePricing } = useCMS();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '', price: '', description: '', features: [], is_popular: false, order: 0
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => { loadPlans(); }, []);

  const loadPlans = async () => {
    try {
      const data = await getPricing(false);
      setPlans(data);
    } catch (e) {
      toast.error('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updatePricing(editingId, formData);
        toast.success('Pricing plan updated');
      } else {
        await createPricing(formData);
        toast.success('Pricing plan created');
      }
      resetForm();
      loadPlans();
    } catch (e) {
      toast.error('Failed to save pricing plan');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pricing plan?')) return;
    try {
      await deletePricing(id);
      toast.success('Pricing plan deleted');
      loadPlans();
    } catch (e) {
      toast.error('Failed to delete pricing plan');
    }
  };

  const handleEdit = (plan) => {
    setFormData({
      name: plan.name,
      price: plan.price,
      description: plan.description,
      features: plan.features || [],
      is_popular: plan.is_popular,
      order: plan.order
    });
    setEditingId(plan.id);
    setShowForm(true);
  };

  const handleToggleActive = async (plan) => {
    try {
      await updatePricing(plan.id, { is_active: !plan.is_active });
      toast.success(`Plan ${plan.is_active ? 'hidden' : 'shown'}`);
      loadPlans();
    } catch (e) {
      toast.error('Failed to update plan');
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({ ...prev, features: [...prev.features, newFeature.trim()] }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
  };

  const resetForm = () => {
    setFormData({ name: '', price: '', description: '', features: [], is_popular: false, order: 0 });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return <div className="text-text-secondary">Loading...</div>;

  return (
    <div className="space-y-6">
      <Toaster position="top-right" richColors />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Pricing Plans</h1>
          <p className="text-text-secondary mt-1">Manage your pricing tiers and features</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-tech-blue hover:bg-tech-blue/90">
          <Plus size={18} className="mr-2" />
          Add Plan
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-lg border-border-line my-8">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle>{editingId ? 'Edit' : 'Add'} Pricing Plan</CardTitle>
              <button onClick={resetForm} className="text-text-secondary hover:text-text-primary">
                <X size={20} />
              </button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Plan Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      placeholder="e.g., Starter"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Price *</label>
                    <input
                      type="text"
                      required
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      placeholder="e.g., $15,000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Description *</label>
                  <textarea
                    required
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary resize-none"
                    placeholder="Brief description of the plan..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Order</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_popular}
                        onChange={(e) => setFormData({ ...formData, is_popular: e.target.checked })}
                        className="w-4 h-4 rounded border-border-line text-tech-blue focus:ring-tech-blue"
                      />
                      <span className="text-sm text-text-secondary">Mark as Popular</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Plan Features</label>
                  <div className="flex space-x-2 mb-2">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                      className="flex-1 px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      placeholder="Add feature..."
                    />
                    <Button type="button" onClick={addFeature} variant="outline">
                      <Plus size={18} />
                    </Button>
                  </div>
                  <ul className="space-y-2 max-h-40 overflow-y-auto">
                    {formData.features.map((feature, index) => (
                      <li key={index} className="flex items-center justify-between p-2 bg-page-bg rounded">
                        <span className="text-sm text-text-primary flex items-center">
                          <Check size={14} className="text-care-teal mr-2" />
                          {feature}
                        </span>
                        <button type="button" onClick={() => removeFeature(index)} className="text-red-500 hover:text-red-600">
                          <X size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" className="bg-tech-blue hover:bg-tech-blue/90">
                    <Save size={18} className="mr-2" />
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.sort((a, b) => a.order - b.order).map((plan) => (
          <Card 
            key={plan.id} 
            className={`border-border-line relative ${!plan.is_active ? 'opacity-50' : ''} ${plan.is_popular ? 'border-tech-blue border-2' : ''}`}
          >
            {plan.is_popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-tech-blue text-white text-xs px-3 py-1 rounded-full flex items-center">
                  <Star size={12} className="mr-1 fill-white" />
                  Most Popular
                </span>
              </div>
            )}
            <CardContent className="p-6 pt-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-text-secondary">Order: {plan.order}</span>
                <button
                  onClick={() => handleToggleActive(plan)}
                  className={`text-xs px-2 py-1 rounded ${plan.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}
                >
                  {plan.is_active ? 'Active' : 'Hidden'}
                </button>
              </div>
              
              <h3 className="text-xl font-bold text-text-primary mb-1">{plan.name}</h3>
              <div className="text-3xl font-bold text-tech-blue mb-2">{plan.price}</div>
              <p className="text-sm text-text-secondary mb-4">{plan.description}</p>
              
              <ul className="space-y-2 mb-4">
                {plan.features?.slice(0, 5).map((feature, index) => (
                  <li key={index} className="text-sm text-text-secondary flex items-start">
                    <Check size={14} className="text-care-teal mr-2 mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
                {plan.features?.length > 5 && (
                  <li className="text-sm text-tech-blue">+{plan.features.length - 5} more features</li>
                )}
              </ul>

              <div className="flex justify-end space-x-2 pt-4 border-t border-border-line">
                <button onClick={() => handleEdit(plan)} className="p-2 text-text-secondary hover:text-tech-blue">
                  <Edit2 size={16} />
                </button>
                <button onClick={() => handleDelete(plan.id)} className="p-2 text-text-secondary hover:text-red-500">
                  <Trash2 size={16} />
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {plans.length === 0 && (
        <div className="text-center py-12 text-text-secondary">
          No pricing plans yet. Click "Add Plan" to create one.
        </div>
      )}
    </div>
  );
};

export default AdminPricingPage;
